import { z } from "zod";

import { DishType, KitchenType, MealType, SensitiveIngredient } from "./Dish";
import { hasPermission, PermissionKey, PermissionScope, type PermissionMap } from "./TeamPermission";

export const hasGlobalDishAgentPermissions = (permissions: PermissionMap): boolean =>
  hasPermission(permissions, PermissionKey.NUTRITION_INGREDIENTS_APPROVE, PermissionScope.ALL) &&
  hasPermission(permissions, PermissionKey.AGENTS_USE, PermissionScope.ALL);

export const dishIssueAgentChangesSchema = z
  .object({
    name: z.string().min(1).max(500).optional(),
    ingredients: z.array(z.unknown()).max(500).nullable().optional(),
    preparationSteps: z.array(z.string().max(5_000)).max(100).optional(),
    tags: z.array(z.string().max(100)).max(100).nullable().optional(),
    dishTypes: z.array(z.enum(DishType)).max(100).nullable().optional(),
    includes: z.array(z.enum(SensitiveIngredient)).max(100).nullable().optional(),
    kitchenTypes: z.array(z.enum(KitchenType)).max(100).nullable().optional(),
    mealTypes: z.array(z.enum(MealType)).max(100).nullable().optional(),
    prepTime: z
      .number()
      .min(0)
      .max(24 * 60)
      .nullable()
      .optional(),
    cookTime: z
      .number()
      .min(0)
      .max(24 * 60)
      .nullable()
      .optional(),
    portions: z.number().positive().max(10_000).nullable().optional(),
    media: z.array(z.unknown()).max(100).nullable().optional(),
  })
  .strict()
  .refine((changes) => Object.keys(changes).length > 0, "At least one dish change is required");

export const dishIssueAgentTaskSaveSchema = z
  .object({
    taskId: z.string().min(1).max(512),
    stepId: z.string().min(1).max(512),
    changes: dishIssueAgentChangesSchema,
    dishId: z.string().uuid(),
    issueId: z.string().uuid(),
  })
  .strict();

export type DishIssueAgentChanges = z.infer<typeof dishIssueAgentChangesSchema>;
export type DishIssueAgentTaskSave = z.infer<typeof dishIssueAgentTaskSaveSchema>;

export const stableSerializeDishIssueValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerializeDishIssueValue).join(",")}]`;
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) =>
      left.localeCompare(right),
    );
    return `{${entries
      .map(([key, nestedValue]) => `${JSON.stringify(key)}:${stableSerializeDishIssueValue(nestedValue)}`)
      .join(",")}}`;
  }

  return JSON.stringify(value) ?? "undefined";
};
